import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Play, Target, Clock, Thermometer } from 'lucide-react';

interface RunSuggestionCardProps {
  suggestion: {
    type: string;
    when: string;
    distance_km: number;
    duration_minutes: number;
    target_pace_min_per_km: number;
    effort_level: string;
  };
  weatherData?: {
    current?: {
      temperature: number;
    };
  };
}

const RunSuggestionCardInner: React.FC<RunSuggestionCardProps> = ({ suggestion, weatherData }) => {
  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getRunTypeColor = (type: string) => {
    switch (type) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'tempo': return 'bg-orange-100 text-orange-800';
      case 'interval': return 'bg-red-100 text-red-800';
      case 'long': return 'bg-blue-100 text-blue-800';
      case 'recovery': return 'bg-purple-100 text-purple-800';
      case 'race': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg">
      <div className="flex items-start gap-3">
        <Play className="h-6 w-6 text-purple-600 mt-1 flex-shrink-0" />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Badge className={getRunTypeColor(suggestion.type)} variant="secondary">
              {suggestion.type.charAt(0).toUpperCase() + suggestion.type.slice(1)} Run
            </Badge>
            <Badge variant="outline" className="text-xs">
              {suggestion.when}
            </Badge>
            {weatherData?.current && (
              <Badge variant="outline" className="text-xs flex items-center gap-1">
                <Thermometer className="h-3 w-3" />
                {Math.round(weatherData.current.temperature)}°F
              </Badge>
            )}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-1">
              <Target className="h-4 w-4 text-gray-500" />
              <span className="font-medium">{suggestion.distance_km}km</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4 text-gray-500" />
              <span className="font-medium">{formatTime(suggestion.duration_minutes)}</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-500">Pace:</span>
              <span className="font-medium ml-1">{suggestion.target_pace_min_per_km}:00/km</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-500">Effort:</span>
              <span className="font-medium ml-1">{suggestion.effort_level}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const RunSuggestionCard = React.memo(RunSuggestionCardInner);